// VisitRow (job page round): one visit in a job's Visits. On the left the board's own VisitBlock, in a
// slot (the board draws it, so a visit looks the same everywhere: window, who, its state); beside it
// only what the block can't say: the day and the work, what happened or what to know, and the
// instructions for the techs. "You came from this visit" when the page was opened from it on the board.
// A button at the end opens its times (a LineTable) and its actions (Links), lined up under the facts;
// the row's words open it too, for the pointer. Reads only --vrow-*; the chevron is the Area's.
import { useId, useState, type ReactNode } from 'react';
import { AreaChevron } from '../Area/Area.tsx';
import './VisitRow.css';

export interface VisitRowProps {
  /** "Visit 2", for its button's name. */
  name: string;
  /** The board's VisitBlock (a slot: the board owns it). */
  block: ReactNode;
  /** The day and the work: "Sat Oct 24 · Install option B". */
  what: string;
  /** What happened, or is to know: "Needs parts: the 50-gal heater wasn’t on the truck". */
  how?: ReactNode;
  /** For the techs: "Bring the 50-gal from the shop". */
  instructions?: string;
  /** The page was opened from this visit. */
  cameFrom?: boolean;
  /** Its times and actions, shown when it's open. */
  children?: ReactNode;
  /** What the button opens, for its name ("times and actions"). */
  opens?: string;
  defaultOpen?: boolean;
  id?: string;
}

export function VisitRow({
  name,
  block,
  what,
  how,
  instructions,
  cameFrom,
  children,
  opens = 'times and actions',
  defaultOpen = false,
  id,
}: VisitRowProps) {
  const [open, setOpen] = useState(defaultOpen);
  const more = useId();
  const has = children !== undefined && children !== null && children !== false;
  return (
    <div id={id} className="fl-vrow" data-open={open ? '' : undefined}>
      <div className="fl-vrow__row">
        <div className="fl-vrow__block">{block}</div>
        {/* the words open it too, for the pointer; the keyboard has the button */}
        <div
          className="fl-vrow__facts"
          data-opens={has ? '' : undefined}
          onClick={has ? () => setOpen(!open) : undefined}
        >
          <p className="fl-vrow__what">
            {what}
            {cameFrom ? <span className="fl-vrow__came">You came from this visit</span> : null}
          </p>
          {how ? <p className="fl-vrow__how">{how}</p> : null}
          {instructions ? <p className="fl-vrow__ins">{instructions}</p> : null}
        </div>
        {has ? (
          <button
            type="button"
            className="fl-vrow__toggle"
            aria-expanded={open}
            aria-controls={more}
            aria-label={`${name}: ${opens}`}
            onClick={() => setOpen(!open)}
          >
            <AreaChevron open={open} />
          </button>
        ) : null}
      </div>
      {has ? (
        <div id={more} className="fl-vrow__more" hidden={!open}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** A visit's actions, under its times: Links in a row. */
export function VisitLinks({ children }: { children: ReactNode }) {
  return <div className="fl-vrow__links">{children}</div>;
}
